import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ContentCenter, Form } from '../../../components/commons';
import { useTheme } from 'styled-components';
import { api } from '../../../services/api';
import { DashStyles, ChartStyle, ChartTitle, Container } from '../Home/styles';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';
import { useForm } from 'react-hook-form';
import SelectInput from '../../../components/Input/SelectInput';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import { FilterStyles } from '../Home/styles';
import { LoaderComponent } from '../../../components/Table/styles';
import Table from '../../../components/Table';
import useDocuments from '../../../services/documents.service';
import useDocumentTypes from '../../../services/documentTypes.service';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const FoldersLevel = ({ control, level, parentId }: { control: any, level: number, parentId?: string }) => {
  const [search, setSearch] = React.useState<string>();
  const { isLoading, data } = useDocuments(1, 18, search, undefined, parentId ? [parentId] : [], {
    isFolder: true,
  });

  return (
    <SelectInput
      control={control}
      onChangeText={(s) => setSearch(s)}
      options={data?.data.map((document) => ({
        label: document?.name,
        value: document?._id,
      }))}
      isLoading={isLoading}
      placeholder={level === 0 ? 'Selecionar Pastas' : 'Selecionar Subpastas'}
      isMulti
      gray
      name={`folders[${level}]`}
    />
  );
};

const CascadingFolders = ({ control, watch }: { control: any; watch: any }) => {
  const formValues = watch();
  const foldersLevels = Object.values(formValues?.folders || {});

  return (
    <>
      <FoldersLevel control={control} level={0} />
      {foldersLevels.map((selectedOptions: any, index: number) => {
        if (selectedOptions && selectedOptions.length === 1) {
          return <FoldersLevel key={index + 1} control={control} level={index + 1} parentId={selectedOptions[0].value} />;
        }
        return null;
      })}
    </>
  );
};

const DocumentTypesList = ({ control }: { control: any }) => {
  const [search, setSearch] = React.useState<string>();
  const { isLoading, data } = useDocumentTypes(1, 18, search, '');

  return (
    <SelectInput
      control={control}
      onChangeText={(s) => setSearch(s)}
      options={data?.data.map((docType: any) => ({
        label: docType?.name,
        value: docType?._id,
      }))}
      isLoading={isLoading}
      placeholder='Selecionar Tipo de Documento'
      isMulti
      gray
      name={'documentTypes'}
    />
  );
};

const Filters = ({ handleGetStatistics }: { handleGetStatistics: (date: any, documentTypes: any, folders: any) => void }) => {
  const { control, reset, watch, formState: { errors } } = useForm<any>();
  const formValues = watch();
  const dateStart: any = formValues?.dateStart;
  const dateEnd: any = formValues?.dateEnd;
  const documentTypes: any = formValues?.documentTypes;
  const folders: any = formValues?.folders;

  React.useEffect(() => {
    let validFolders: any[] = [];
    const foldersLevels = Object.values(folders || {});
    if (foldersLevels[0]) validFolders = [...(foldersLevels[0] as any[])];
    for (let i = 0; i < foldersLevels.length; i++) {
      const currentLevel = foldersLevels[i] as any[];
      const nextLevel = foldersLevels[i + 1] as any[];
      if (currentLevel && currentLevel.length === 1 && nextLevel && nextLevel.length > 0) {
        validFolders = [...nextLevel];
      } else if (currentLevel && currentLevel.length !== 1) {
        break;
      }
    }
    
    handleGetStatistics({ startDate: dateStart, endDate: dateEnd }, documentTypes, validFolders);
  }, [dateStart, dateEnd, JSON.stringify(documentTypes), JSON.stringify(folders)]);

  return (
    <FilterStyles.Content>
      <FilterStyles.Title>Filtros</FilterStyles.Title>
      <Form>
        <div style={{ display: 'flex', gap: 10 }}>
          <Input control={control} name={'dateStart'} gray type='date' placeholder='Data Início' />
          <Input control={control} name={'dateEnd'} gray type='date' placeholder='Data Fim' />
        </div>
        <DocumentTypesList control={control} />
        <CascadingFolders control={control} watch={watch} />
        <ContentCenter>
          <Button label='Limpar' onClick={() => reset()} color='primary' variantType='small' />
        </ContentCenter>
      </Form>
    </FilterStyles.Content>
  );
};

const Chart = ({ title, total = 0, filter = 0, warning = false }: { title: string; total?: number; filter?: number; warning?: boolean }) => {
  const theme: any = useTheme();
  const data = {
    labels: ['Total', 'Filtros'],
    datasets: [{
      label: 'Quantidade',
      data: [total, filter],
      backgroundColor: [theme.colors.primary, theme.colors.secondary],
      borderColor: [theme.colors.primary, theme.colors.secondary],
      borderWidth: 1,
    }],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <ChartStyle.Content>
      <div style={{ padding: 10, width: '100%', maxWidth: 300, margin: 'auto', textAlign: 'center' }}>
        <ChartTitle>{title}{warning ? '*' : ''}</ChartTitle>
        <div style={{ marginTop: 10 }}>
          <Bar data={data} options={options} />
        </div>
        <div style={{ marginTop: 15, display: 'flex', justifyContent: 'space-between', color: theme.colors.text }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 12, height: 12, backgroundColor: theme.colors.primary, borderRadius: '50%' }} />
            <span>Total: <strong>{total}</strong></span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 12, height: 12, backgroundColor: theme.colors.secondary, borderRadius: '50%' }} />
            <span>Filtros: <strong>{filter}</strong></span>
          </div>
        </div>
      </div>
    </ChartStyle.Content>
  );
};

const Reports: React.FC = () => {
  const mobile = useIsMobile();
  const nav = useNavigate();
  const theme: any = useTheme();
  const [statistics, setStatistics] = React.useState<any>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const handleGetStatistics = async (date: any, documentTypes: any, folders: any) => {
    setIsLoading(true);
    try {
      const { data: response } = await api.get('/dashboard/statistics', {
        params: {
          dateStart: date?.startDate,
          dateEnd: date?.endDate,
          documentTypes: JSON.stringify(documentTypes?.map((a: any) => a.value) || []),
          folders: JSON.stringify(folders?.map((f: any) => f.value) || []),
        },
      });
      setStatistics(response);
    } catch (error) {
      nav('/documents');
    } finally {
      setIsLoading(false);
    }
  };

  const missingPages = statistics?.missingPagesFilterCount > 0;

  const columns = React.useMemo(
    () => [
      {
        header: 'Tipo de Documento',
        accessorKey: 'name',
      },
      {
        header: 'Documentos',
        accessorKey: 'documentsCount',
      },
      {
        header: 'Páginas',
        accessorKey: 'pagesCount',
      },
    ],
    []
  );

  return (
    <Container>
      <DashStyles.RightPanel hidden={!mobile}>
        <Filters handleGetStatistics={handleGetStatistics} />
      </DashStyles.RightPanel>

      <DashStyles.LeftPanel>
        <div style={{ padding: '0 20px' }}>
          <h1 style={{ color: theme.colors.title, margin: '20px 0', fontSize: 24 }}>Relatórios e Estatísticas</h1>
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', flex: 1, height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <LoaderComponent />
          </div>
        ) : (
          <>
            <Chart title="Documentos" total={statistics?.documentsTotal} filter={statistics?.documentsFilter} />
            <Chart title="Páginas" total={statistics?.pagesTotal} filter={statistics?.pagesFilter} warning={missingPages} />

            <div style={{ padding: '20px', width: '100%', overflowX: 'auto' }}>
              <h2 style={{ color: theme.colors.title, margin: '20px 0', fontSize: 20 }}>Resumo por Tipo</h2>
              {statistics?.typeBreakdown ? (
                <Table
                  columns={columns}
                  data={statistics.typeBreakdown}
                  isLoading={isLoading}
                />
              ) : (
                <div style={{ padding: '20px', color: theme.colors.placeholder, textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: '10px' }}>
                  Selecione algum filtro (pasta, data ou tipo) para exibir a tabela de resumo por Tipo de Documento.
                </div>
              )}
            </div>

            {missingPages && (
              <div style={{ padding: '20px', color: '#ff4d4f', fontWeight: 'bold' }}>
                (*) {statistics.missingPagesFilterCount} documento(s) no filtro não tiveram sua quantidade de páginas calculada.
              </div>
            )}
          </>
        )}
      </DashStyles.LeftPanel>

      <DashStyles.RightPanel hidden={mobile}>
        <Filters handleGetStatistics={handleGetStatistics} />
      </DashStyles.RightPanel>
    </Container>
  );
};

export default Reports;
